/**
 * DL-04 Part A — "Download catalogue (JSON)" on the catalogue page.
 *
 * Platform admin only (the export route answers 403 for every other role;
 * this only hides the control). Saves the served
 * `aeriosiq.defect-catalogue` 1.0 file verbatim as
 * `catalogue_<owner>_<name>_v<version>.json`.
 *
 * Which version: the one picked in the Versions tab, else the catalogue's
 * current PUBLISHED version — the one a data set's `bundle.json` names
 * (`data_acquisition.catalogue.version_id`) — else the version the editor
 * shows. The editor itself opens the draft by default; exporting that would
 * hand Thorsten a version no bundle refers to.
 */
import type { ReactElement } from "react";
import { Button } from "@mui/material";
import { useHasRole } from "@aeriosiq/auth-client";
import type { CatalogueExport } from "@aeriosiq/types";
import { useExportCatalogueVersionMutation } from "./taxonomyQueries.js";

export interface CatalogueExportButtonProps {
  catalogueId: string;
  /** The version the export targets (see the header); null = nothing to export. */
  versionId: string | null;
}

/** A file-name segment: lower-case, `[a-z0-9-]` only, never empty. */
export function fileSegment(
  raw: string | null | undefined,
  fallback: string,
): string {
  const s = (raw ?? "")
    .normalize("NFKD")
    // strip the combining marks NFKD split off: "Ö" → "O"
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return s !== "" ? s : fallback;
}

export function catalogueExportFileName(e: CatalogueExport): string {
  const c = e.catalogue;
  return `catalogue_${fileSegment(c.owner_tenant, "tenant")}_${fileSegment(c.name.en, "catalogue")}_v${fileSegment(c.version, "0")}.json`;
}

/** Hand the JSON to the browser as a download (no navigation). */
export function saveJson(fileName: string, body: unknown): void {
  const blob = new Blob([`${JSON.stringify(body, null, 2)}\n`], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function CatalogueExportButton({
  catalogueId,
  versionId,
}: CatalogueExportButtonProps): ReactElement | null {
  const isPlatformAdmin = useHasRole("platform_admin");
  const exportVersion = useExportCatalogueVersionMutation(catalogueId);
  if (!isPlatformAdmin) return null;
  return (
    <Button
      size="small"
      variant="outlined"
      data-testid="catalogue-export-json"
      disabled={versionId === null || exportVersion.isPending}
      onClick={() => {
        if (versionId === null) return;
        exportVersion.mutate(versionId, {
          onSuccess: (e) => {
            saveJson(catalogueExportFileName(e), e);
          },
        });
      }}
    >
      Download catalogue (JSON)
    </Button>
  );
}
